import {
	IconCheck,
	IconChevronLeft,
	IconChevronRight,
	IconDotsVertical,
	IconExternalLink,
	IconFilterOff,
	IconRefresh,
} from "@tabler/icons-react";
import {
	type ColumnDef,
	type ColumnFiltersState,
	flexRender,
	getCoreRowModel,
	getFacetedRowModel,
	getFacetedUniqueValues,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	type PaginationState,
	type SortingState,
	useReactTable,
	type VisibilityState,
} from "@tanstack/react-table";
import type { MenuProps, TableColumnsType } from "antd";
import {
	Button,
	Checkbox,
	Dropdown,
	Flex,
	Grid,
	Space,
	Table,
	Tooltip,
	Typography,
	theme,
} from "antd";
import type React from "react";
import { useCallback, useMemo, useState } from "react";
import { BaseContainer, CustomIcon } from "./base";

interface TablePaginationProps {
	totalCount?: number;
	hasNextPage?: boolean;
	hasPreviousPage?: boolean;
	onNextPage?: () => void;
	onPreviousPage?: () => void;
	pageSize?: number;
}

interface SelectionAction<TData> {
	label: string;
	icon?: React.ReactElement;
	onClick: (rows: TData[]) => void;
	variant?: "default" | "destructive" | "outline";
	disabled?: boolean;
}

interface FilterHelpers {
	setFilter: (columnId: string, value: unknown) => void;
	getFilter: (columnId: string) => unknown;
}

export interface RowAction<TData> {
	label: string | ((row: TData) => string);
	icon?: React.ReactElement;
	danger?: boolean | ((row: TData) => boolean);
	disabled?: boolean | ((row: TData) => boolean);
	onClick: (row: TData) => void;
}

export interface ResponsiveTableProps<TData> extends TablePaginationProps {
	data: TData[];
	columns: ColumnDef<TData>[];
	loading?: boolean;
	/** When true, the table does not sort/filter rows itself — the server does. */
	manualSorting?: boolean;
	/** Called with the new sort when a column header is toggled (server-side sort). */
	onSortingChange?: (sorting: SortingState) => void;
	/** 0-based current page index (server pagination) — drives the "Page X of Y" footer. */
	pageIndex?: number;
	compactRender?: (row: TData, index: number) => React.ReactNode;
	onRowClick?: (row: TData) => void;
	/** When set, rows get an "Open in new window" action (dropdown + right-click) linking to this URL. */
	rowHref?: (row: TData) => string;
	initialColumnVisibility?: VisibilityState;
	initialSorting?: SortingState;
	getRowClassName?: (row: TData) => string;
	getRowStyle?: (row: TData) => React.CSSProperties;
	selectionAction?: SelectionAction<TData>;
	filterBar?: (helpers: FilterHelpers) => React.ReactNode;
	toolbarActions?: React.ReactNode;
	onRefresh?: () => void;
	rowActions?: RowAction<TData>[];
	/** Desktop only: replaces the table with a custom view of the current page's rows (toolbar stays).
	 *  `footer` is the table's selection/pagination footer, for the view to place. */
	renderBody?: (rows: TData[], footer: React.ReactNode) => React.ReactNode;
}

export function ResponsiveTable<TData extends object>({
	data,
	columns,
	loading,
	compactRender: mobileRender,
	onRowClick,
	rowHref,
	initialColumnVisibility,
	initialSorting,
	getRowClassName,
	getRowStyle,
	hasNextPage,
	hasPreviousPage,
	onNextPage,
	onPreviousPage,
	pageSize = 5,
	manualSorting,
	onSortingChange,
	pageIndex,
	totalCount,
	selectionAction,
	filterBar,
	toolbarActions,
	onRefresh,
	rowActions,
	renderBody,
}: ResponsiveTableProps<TData>) {
	const screens = Grid.useBreakpoint();
	const showCompact = !screens.lg;
	const isServerPaginated = !!(onNextPage || onPreviousPage);
	// Server mode: rows arrive pre-sorted/pre-filtered from the API; don't re-process them client-side.
	const serverMode = isServerPaginated || !!manualSorting;
	const [sorting, setSorting] = useState<SortingState>(initialSorting ?? []);
	const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
	const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
		initialColumnVisibility || {},
	);
	const [rowSelection, setRowSelection] = useState({});
	const [pagination, setPagination] = useState<PaginationState>({
		pageIndex: 0,
		pageSize,
	});
	const {
		token: {
			colorBgContainer,
			colorBorder,
			colorBorderSecondary,
			borderRadius,
			lineWidth,
			lineType,
			padding,
		},
	} = theme.useToken();

	// Row right-clicked on desktop — drives the shared context menu below.
	const [contextRow, setContextRow] = useState<TData | null>(null);

	// Shared menu items for both the three-dots dropdown and the right-click context menu.
	// An "Open in new window" entry is prepended whenever rowHref is provided.
	const buildActionItems = useCallback(
		(row: TData): MenuProps["items"] => {
			const items: MenuProps["items"] = [];
			if (rowHref) {
				items.push({
					key: "_open",
					label: "Open in new window",
					icon: <CustomIcon icon={IconExternalLink} />,
					onClick: ({ domEvent }) => {
						domEvent.stopPropagation();
						window.open(rowHref(row), "_blank", "noopener");
					},
				});
			}
			(rowActions ?? []).forEach((action, i) => {
				items.push({
					key: i,
					label:
						typeof action.label === "function"
							? action.label(row)
							: action.label,
					icon: action.icon,
					danger:
						(typeof action.danger === "function"
							? action.danger(row)
							: action.danger) || false,
					disabled:
						typeof action.disabled === "function"
							? action.disabled(row)
							: action.disabled,
					onClick: ({ domEvent }) => {
						domEvent.stopPropagation();
						action.onClick(row);
					},
				});
			});
			return items;
		},
		[rowActions, rowHref],
	);

	const renderRowActions = useCallback(
		(row: TData) => (
			<Dropdown trigger={["click"]} menu={{ items: buildActionItems(row) }}>
				<Button
					size="small"
					type="text"
					icon={<CustomIcon icon={IconDotsVertical} />}
					onClick={(e) => e.stopPropagation()}
				/>
			</Dropdown>
		),
		[buildActionItems],
	);

	// Show the actions column / context menu when there are explicit actions or an open-in-new-window target.
	const hasActions = !!(rowActions?.length || rowHref);

	const allColumns = useMemo<ColumnDef<TData>[]>(() => {
		const base = selectionAction
			? [
					{
						id: "_select",
						size: 40,
						header: ({ table }) => (
							<Checkbox
								checked={table.getIsAllPageRowsSelected()}
								indeterminate={table.getIsSomePageRowsSelected()}
								onChange={(e) =>
									table.toggleAllPageRowsSelected(e.target.checked)
								}
							/>
						),
						cell: ({ row }) => (
							<Checkbox
								checked={row.getIsSelected()}
								onChange={(e) => row.toggleSelected(e.target.checked)}
								onClick={(e) => e.stopPropagation()}
							/>
						),
					} as ColumnDef<TData>,
					...columns,
				]
			: columns;

		if (!hasActions) return base;

		return [
			...base,
			{
				id: "_actions",
				size: 40,
				header: () => null,
				cell: ({ row }) => renderRowActions(row.original),
			} as ColumnDef<TData>,
		];
	}, [columns, selectionAction, hasActions, renderRowActions]);

	const table = useReactTable({
		data,
		columns: allColumns,
		// ponytail: tanstack defaults an unset column `size` to 150, which defeats
		// "leave size unset so this column grows" — override the default to undefined
		// so antd's width (below) stays undefined and the column fills leftover space.
		defaultColumn: { size: undefined },
		state: {
			sorting,
			columnFilters,
			columnVisibility,
			rowSelection,
			...(!isServerPaginated ? { pagination } : {}),
		},
		manualSorting: serverMode,
		manualFiltering: serverMode,
		onSortingChange: setSorting,
		onColumnFiltersChange: setColumnFilters,
		onColumnVisibilityChange: setColumnVisibility,
		onRowSelectionChange: setRowSelection,
		...(!isServerPaginated ? { onPaginationChange: setPagination } : {}),
		getCoreRowModel: getCoreRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFacetedRowModel: getFacetedRowModel(),
		getFacetedUniqueValues: getFacetedUniqueValues(),
		...(!isServerPaginated
			? { getPaginationRowModel: getPaginationRowModel() }
			: {}),
	});

	const selectedRows = table.getSelectedRowModel().rows.map((r) => r.original);
	const hasSelection = selectedRows.length > 0;

	const baseActions = (
		<>
			<div>
				{selectionAction && (
					<Tooltip title={selectionAction.label}>
						<Button
							size="medium"
							type="text"
							disabled={!hasSelection || selectionAction.disabled}
							danger={selectionAction.variant === "destructive"}
							onClick={() => {
								selectionAction.onClick(selectedRows);
								setRowSelection({});
							}}
							icon={selectionAction.icon}
						></Button>
					</Tooltip>
				)}
			</div>
			<div style={{ display: "flex", alignItems: "center", gap: 4 }}>
				{toolbarActions}
				{onRefresh && (
					<Tooltip title={"Refresh"}>
						<Button
							loading={loading}
							size="medium"
							type="text"
							onClick={onRefresh}
							icon={<CustomIcon icon={IconRefresh} />}
						/>
					</Tooltip>
				)}
			</div>
		</>
	);

	const filterHelpers: FilterHelpers = {
		setFilter: (columnId, value) =>
			setColumnFilters((prev) => {
				const rest = prev.filter((f) => f.id !== columnId);
				return value !== undefined && value !== "" && value !== null
					? [...rest, { id: columnId, value }]
					: rest;
			}),
		getFilter: (columnId) =>
			columnFilters.find((f) => f.id === columnId)?.value,
	};

	// Build antd columns from tanstack header groups
	const antdColumns: TableColumnsType<TData> =
		table.getHeaderGroups()[0]?.headers.map((header, colIndex) => {
			const canSort = header.column.getCanSort();
			const sortOrder = header.column.getIsSorted();
			return {
				key: header.id,
				dataIndex: header.id,
				width: header.column.columnDef.size,
				title: header.isPlaceholder
					? null
					: flexRender(header.column.columnDef.header, header.getContext()),
				sorter: canSort ? true : undefined,
				sortOrder: canSort
					? sortOrder === "asc"
						? "ascend"
						: sortOrder === "desc"
							? "descend"
							: null
					: undefined,
				render: (_: any, _record: TData, index: number) => {
					const row = table.getRowModel().rows[index];
					if (!row) return null;
					const cell = row
						.getVisibleCells()
						.find((c) => c.column.id === header.id);
					if (!cell) return null;
					return flexRender(cell.column.columnDef.cell, cell.getContext());
				},
				onCell: (_record: TData, index?: number) => {
					const original =
						index !== undefined
							? table.getRowModel().rows[index]?.original
							: undefined;
					const rowStyle = original && getRowStyle ? getRowStyle(original) : {};
					const isFirst = colIndex === 0;
					return {
						onClick: () => original && onRowClick?.(original),
						style: {
							cursor: onRowClick ? "pointer" : undefined,
							...(isFirst ? rowStyle : {}),
						},
						className: original ? (getRowClassName?.(original) ?? "") : "",
					};
				},
			};
		}) ?? [];

	const hasToolbar = !!(
		filterBar ||
		selectionAction ||
		toolbarActions ||
		onRefresh
	);

	// Minimum width the columns need. When the container is wider, the table fills 100%
	// (columns expand); when it's narrower, antd scrolls horizontally instead of clipping
	// the last column / breaking the rounded corner. Flexible (size-less) columns get a
	// sensible default so they don't collapse when scrolling.
	const scrollX = antdColumns.reduce(
		(sum, c) => sum + (typeof c.width === "number" ? c.width : 160),
		0,
	);

	// Selection count + pagination controls — the table's footer, also handed to renderBody.
	const footer = (
		<Flex justify="space-between" style={{ minHeight: "20px" }}>
			<div>
				{selectionAction && (
					<Flex gap={8}>
						{hasSelection && (
							<Flex gap={2}>
								<CustomIcon icon={IconCheck} />
								<Typography.Text
									strong
									type="secondary"
									style={{
										fontSize: "0.8rem",
									}}
								>
									{selectedRows.length} selected
								</Typography.Text>
							</Flex>
						)}
					</Flex>
				)}
			</div>
			{(isServerPaginated || hasNextPage || hasPreviousPage) && (
				<div style={{ display: "flex", alignItems: "center", gap: 4 }}>
					{isServerPaginated ? (
						<Typography.Text type="secondary" style={{ fontSize: "0.8rem" }}>
							Page {(pageIndex ?? 0) + 1} of{" "}
							{Math.max(1, Math.ceil((totalCount ?? 0) / (pageSize || 1)))} ·{" "}
							{totalCount ?? 0} items
						</Typography.Text>
					) : (
						<Typography.Text type="secondary" style={{ fontSize: "0.8rem" }}>
							{pagination.pageIndex * pagination.pageSize + 1}–
							{Math.min(
								(pagination.pageIndex + 1) * pagination.pageSize,
								table.getFilteredRowModel().rows.length,
							)}{" "}
							of {table.getFilteredRowModel().rows.length}
						</Typography.Text>
					)}
					<Button
						size="small"
						type="text"
						disabled={
							isServerPaginated ? !hasPreviousPage : !table.getCanPreviousPage()
						}
						onClick={
							isServerPaginated ? onPreviousPage : () => table.previousPage()
						}
						icon={<CustomIcon icon={IconChevronLeft} />}
					>
						Previous
					</Button>
					<Button
						size="small"
						type="text"
						disabled={
							isServerPaginated ? !hasNextPage : !table.getCanNextPage()
						}
						onClick={isServerPaginated ? onNextPage : () => table.nextPage()}
						title="Next"
						iconPlacement="end"
						icon={<CustomIcon icon={IconChevronRight} />}
					>
						Next
					</Button>
				</div>
			)}
		</Flex>
	);

	return (
		<BaseContainer>
			{hasToolbar && (
				<Flex
					justify="space-between"
					align="center"
					wrap
					gap={8}
					style={{ width: "100%" }}
				>
					{/* Filters on the left */}
					<Space wrap align="center">
						{filterBar?.(filterHelpers)}
						{columnFilters.length > 0 && (
							<Tooltip title="Reset filters">
								<Button
									type="text"
									icon={<CustomIcon icon={IconFilterOff} />}
									onClick={() => setColumnFilters([])}
								/>
							</Tooltip>
						)}
					</Space>
					{/* Actions on the right */}
					<Space wrap align="center">
						{baseActions}
					</Space>
				</Flex>
			)}

			{/* Mobile */}
			{showCompact && (
				<div
					style={{
						width: "100%",
						backgroundColor: colorBgContainer,
						border: `${lineWidth}px ${lineType} ${colorBorder}`,
						borderRadius: borderRadius,
					}}
				>
					{table.getRowModel().rows.length === 0 ? (
						<div style={{ textAlign: "center", padding: "24px 0" }}>
							No items found
						</div>
					) : (
						table.getRowModel().rows.map((row, index) => (
							<article
								key={row.id}
								onKeyDown={() => onRowClick?.(row.original)}
								onClick={() => onRowClick?.(row.original)}
								style={{
									borderBottom: `${lineWidth}px ${lineType} ${colorBorderSecondary}`,

									// borderRadius,
									cursor: onRowClick ? "pointer" : undefined,
									padding,
									...(getRowStyle ? getRowStyle(row.original) : {}),
								}}
							>
								<Flex justify="space-between" align="center" gap={8}>
									<div style={{ minWidth: 0, flex: "1 1 auto" }}>
										{mobileRender ? mobileRender(row.original, index) : null}
									</div>
									{/* temporarly disable row actions */}
									{/*{rowActions?.length && (
										<div style={{ flexShrink: 0 }}>
											{renderRowActions(row.original)}
										</div>
									)}*/}
								</Flex>
							</article>
						))
					)}
				</div>
			)}

			{!showCompact &&
				renderBody?.(
					table.getRowModel().rows.map((r) => r.original),
					footer,
				)}

			{/* Desktop */}
			{!showCompact && !renderBody && (
				<Dropdown
					trigger={["contextMenu"]}
					disabled={!hasActions}
					menu={{ items: contextRow ? buildActionItems(contextRow) : [] }}
				>
					{/* Wrapper div: antd Table doesn't forward the Dropdown's injected onContextMenu
					    to a DOM node, so the context menu needs a real element to attach to. */}
					<div style={{ width: "100%" }}>
						<Table<TData>
							onRow={(_record, index) => ({
								onContextMenu: () => {
									const row =
										index !== undefined
											? table.getRowModel().rows[index]
											: undefined;
									if (row) setContextRow(row.original);
								},
							})}
							tableLayout="fixed"
							scroll={{ x: scrollX }}
							style={{
								borderColor: colorBorder,
								borderWidth: lineWidth,
								borderStyle: lineType,
								borderRadius: borderRadius,
								width: "100%",
							}}
							columns={antdColumns}
							dataSource={table
								.getRowModel()
								.rows.map((r, i) => ({ ...r.original, _key: i }))}
							rowKey="_key"
							size="small"
							locale={{ emptyText: "No results found" }}
							pagination={false}
							styles={{
								footer: {
									backgroundColor: colorBgContainer,
								},
							}}
							footer={() => footer}
							onChange={(_, __, sorter) => {
								const s = Array.isArray(sorter) ? sorter[0] : sorter;
								const next: SortingState =
									!s || !s.columnKey || !s.order
										? []
										: [
												{
													id: String(s.columnKey),
													desc: s.order === "descend",
												},
											];
								setSorting(next);
								onSortingChange?.(next);
							}}
						/>
					</div>
				</Dropdown>
			)}
		</BaseContainer>
	);
}
